import { CustomCode } from '@betterfunnel/types';
import Badge from '@betterfunnel/ui-core/Badge';
import Button from '@betterfunnel/ui-core/Button';
import Toggle from '@betterfunnel/ui-core/Toggle';
import { ClockIcon, TrashIcon } from '@heroicons/react/24/outline';

interface CustomCodeCardProps {
  customCode: CustomCode;
  source: 'account' | 'campaign' | 'website';
  isInherited?: boolean;
  isOverridden?: boolean;
  displayEnabled?: boolean;
  onToggle?: (codeId: string, enabled: boolean) => void;
  onEdit?: (code: CustomCode) => void;
  onDelete?: (codeId: string) => void;
  readOnly?: boolean;
}

const getBadgeColor = (
  source: 'account' | 'campaign' | 'website',
): 'gray' | 'blue' | 'green' => {
  switch (source) {
    case 'account':
      return 'gray';
    case 'campaign':
      return 'blue';
    case 'website':
      return 'green';
  }
};

export default function CustomCodeCard({
  customCode,
  source,
  isInherited,
  isOverridden,
  displayEnabled,
  onToggle,
  onEdit,
  onDelete,
  readOnly,
}: CustomCodeCardProps) {
  const enabled = displayEnabled ?? customCode.enabled;
  const canEdit = !readOnly && !isInherited;

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <h3 className="truncate font-medium text-gray-900">
              {customCode.name}
            </h3>
            <Badge
              color={getBadgeColor(source)}
              title={source.charAt(0).toUpperCase() + source.slice(1)}
              size="small"
              hideDot
            />
            {isOverridden && (
              <Badge color="yellow" title="Overridden" size="small" hideDot />
            )}
            {isInherited && (
              <Badge color="gray" title="Inherited" size="small" hideDot />
            )}
          </div>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500">
            <span className="inline-flex items-center gap-1">
              <span className="font-medium">Location:</span>
              <span className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs">
                {customCode.location}
              </span>
            </span>
            {customCode.delay !== undefined && customCode.delay > 0 && (
              <span className="inline-flex items-center gap-1">
                <ClockIcon className="h-4 w-4" />
                <span>{customCode.delay}ms delay</span>
              </span>
            )}
          </div>

          <div className="mt-3">
            <pre className="max-h-32 overflow-auto rounded-md bg-gray-50 p-3 text-xs text-gray-800">
              {customCode.code}
            </pre>
          </div>
        </div>

        <div className="flex shrink-0 items-start gap-2">
          <Toggle
            checked={enabled}
            onChange={(checked) => onToggle?.(customCode.id, checked)}
            disabled={!onToggle}
            size="small"
          />
        </div>
      </div>

      {canEdit && (
        <div className="mt-4 flex items-center gap-2 border-t border-gray-100 pt-3">
          <Button
            size="small"
            variant="secondary"
            onClick={() => onEdit?.(customCode)}
          >
            Edit
          </Button>
          <Button
            size="small"
            variant="secondary"
            color="negative"
            onClick={() => onDelete?.(customCode.id)}
          >
            <TrashIcon className="-ml-0.5 h-4 w-4" />
            Delete
          </Button>
        </div>
      )}
    </div>
  );
}
